import React, { useEffect, FC, useState, useContext, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { resetUserDetails } from '@marriott/mi-store-utils';
import { Messages, Modal, RichText } from '@marriott/mi-ui-library';
import { phoenixAccountDttGetOrderByOrderId } from '../../modules/graphV2';
import { constants, generateApolloClientHeaders } from '../../modules/utils';
import { useStore } from '../../modules/store/memberLevelStore';
import { PageContext } from '../../modules/context';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import { SkeletonLoaderV2 } from '../../molecules';
import { CancelTripModalProps, TrackingProperties, OrderByOrderIdProps } from './CancelTripModal.types';
import { StyledCancelTripModal } from './CancelTripModal.styles';
import { updateCancelModalBookPageContext } from './CancelTripModalUtils';

// To refactor this component to use the CancelAllRoomsCTA component
const ensureStringGetSymbol = () => {
  const stringPrototype = String.prototype as string & {
    getSymbol?: () => string;
  };
  if (typeof stringPrototype.getSymbol === 'function') {
    return;
  }
  String.prototype.getSymbol = function () {
    return String(this || '');
  };
};

const sanitizeTrackingProperties = (trackingProperties?: TrackingProperties) => ({
  trackingContentPosition: trackingProperties?.trackingContentPosition ?? undefined,
  trackingOfferType: trackingProperties?.trackingOfferType ?? undefined,
  atCCeVar48: trackingProperties?.atCCeVar48 ?? undefined,
  trackingDescription: trackingProperties?.trackingDescription ?? undefined,
  trackingTag: trackingProperties?.trackingTag ?? undefined,
  clickTrack: trackingProperties?.clickTrack ?? undefined,
  impressionTrack: trackingProperties?.impressionTrack ?? undefined,
  impressionCount: trackingProperties?.impressionCount ?? undefined,
  merchandisingCategory: trackingProperties?.merchandisingCategory ?? undefined,
  additionalTrackingVariables:
    typeof trackingProperties?.additionalTrackingVariables === 'string'
      ? trackingProperties.additionalTrackingVariables
      : undefined,
});

const buildSingleRoomCta = (
  primaryCtaText: string | undefined,
  fallbackCtaText: string | undefined,
  finalFallbackCtaText: string | undefined,
  primaryTracking?: TrackingProperties,
  fallbackTracking?: TrackingProperties
) => ({
  ctaText: primaryCtaText ?? fallbackCtaText ?? finalFallbackCtaText ?? '',
  trackingProperties: sanitizeTrackingProperties(primaryTracking ?? fallbackTracking),
});

export const CancelTripModal: FC<CancelTripModalProps> = props => {
  const pageContext = useContext(PageContext);
  const { IS_LOCAL_DEV } = process.env;
  const [showCancellationError, setShowCancellationError] = useState(false);
  const [isPageContextReady, setIsPageContextReady] = useState(false);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [orderResponse, setOrderResponse] = useState<OrderByOrderIdProps | null>(null);
  const [orderQueryData, setOrderQueryData] = useState<OrderByOrderIdProps | null>(null);
  const uxlMigrationStatusRef = useRef('');
  const latestRequestedConfirmationRef = useRef<string | null>(null);
  const hasFetchedConfirmationRef = useRef<string | null>(null);
  const cancellationFailedRef = useRef(false);
  const { selectedConfirmationNumber, setSelectedConfirmationNumber } = useStore(state => ({
    selectedConfirmationNumber: state.selectedConfirmationNumber,
    setSelectedConfirmationNumber: state.setSelectedConfirmationNumber,
  }));
  const { memberLocalizedName } = useAccountPersistentStore(state => state);
  const uxlFailureErrorMessage = pageContext?.uxlErrorMessage;
  const currentConfNum = selectedConfirmationNumber ?? undefined;
  const show = Boolean(selectedConfirmationNumber);
  const cancelAllRoomsCta = props?.cancelallroomscta;
  const CancelRoomModalComponent = props?.CancelRoomModalComponent;
  const [getOrderByOrderId] = useLazyQuery(phoenixAccountDttGetOrderByOrderId);
  const orderItems = orderQueryData?.commerce?.order?.orderByConfirmationNumber?.items;
  const itemData = Array.isArray(orderItems)
    ? orderItems.find(
        item =>
          (item?.basicInformation?.confirmationNumber ?? '').toString().toLowerCase() ===
          (selectedConfirmationNumber ?? '').toString().toLowerCase()
      ) ??
      orderItems[0] ??
      null
    : null;

  const resetModalViewState = () => {
    setIsPageContextReady(false);
    setShowCancellationError(false);
    setIsOrderLoading(false);
    setOrderResponse(null);
    setOrderQueryData(null);
    latestRequestedConfirmationRef.current = null;
    hasFetchedConfirmationRef.current = null;
    cancellationFailedRef.current = false;
    uxlMigrationStatusRef.current = '';
  };

  useEffect(() => {
    if (!selectedConfirmationNumber) {
      resetModalViewState();
      return;
    }
    if (hasFetchedConfirmationRef.current === selectedConfirmationNumber) {
      return;
    }
    setIsPageContextReady(false);
    setShowCancellationError(false);
    setIsOrderLoading(true);
    setOrderResponse(null);
    setOrderQueryData(null);
    cancellationFailedRef.current = false;
    uxlMigrationStatusRef.current = '';
    latestRequestedConfirmationRef.current = selectedConfirmationNumber;
    hasFetchedConfirmationRef.current = selectedConfirmationNumber;

    getOrderByOrderId({
      variables: {
        input: {
          confirmationNumber: selectedConfirmationNumber,
        },
      },
      context: {
        ...generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
        setUXLMigrationStatus: (headers: Headers) => {
          const status = headers?.get?.(constants.UXL_MIGRATION_STATUS)?.trim() ?? '';
          uxlMigrationStatusRef.current = status;
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        if (latestRequestedConfirmationRef.current !== selectedConfirmationNumber) {
          return;
        }
        setIsOrderLoading(false);
        const typedData = (data as OrderByOrderIdProps) ?? null;
        setOrderResponse(typedData);
        setOrderQueryData(typedData);

        if (!typedData || !CancelRoomModalComponent) {
          setIsPageContextReady(false);
          setShowCancellationError(true);
          return;
        }
        const migrationStatusFromResponse = uxlMigrationStatusRef.current;
        const didSeedContext = updateCancelModalBookPageContext({
          orderResponse: typedData,
          selectedConfirmationNumber,
          setBookPageContext: props?.setBookPageContext,
          bookPageState: props?.bookPageState,
          bookPageContextComponentIds: props?.bookPageContextComponentIds,
          setUXLMigrationStatus: props?.setUXLMigrationStatus,
          migrationStatusFromResponse,
          memberGivenName: memberLocalizedName,
          sessionFirstName: pageContext?.sessionData?.cacheData?.data?.firstName,
          propertyGmtOffset: props?.hotelGmtOffset,
          accountSessionData: {
            ...pageContext?.sessionData,
            cacheData: {
              ...pageContext?.sessionData?.cacheData,
              data: {
                ...pageContext?.sessionData?.cacheData?.data,
                AriesReservation: {
                  ...pageContext?.sessionData?.cacheData?.data?.AriesReservation,
                  ...(migrationStatusFromResponse
                    ? {
                        uxlMigrationStatus: migrationStatusFromResponse,
                        [constants.UXL_MIGRATION_STATUS]: migrationStatusFromResponse,
                      }
                    : {}),
                },
              },
            },
          },
        });
        setIsPageContextReady(didSeedContext);
        if (!didSeedContext) {
          setShowCancellationError(true);
        }
      },
      onError: () => {
        setIsOrderLoading(false);
        setIsPageContextReady(false);
        setOrderResponse(null);
        setOrderQueryData(null);
        setShowCancellationError(true);
      },
    });
  }, [
    selectedConfirmationNumber,
    getOrderByOrderId,
    IS_LOCAL_DEV,
    pageContext,
    memberLocalizedName,
    props?.hotelGmtOffset,
    props?.setBookPageContext,
    props?.bookPageState,
    props?.bookPageContextComponentIds,
    props?.setUXLMigrationStatus,
    CancelRoomModalComponent,
  ]);

  useEffect(() => {
    if (show && isPageContextReady) {
      setShowCancellationError(false);
      ensureStringGetSymbol();
    }
  }, [show, isPageContextReady]);

  useEffect(() => {
    if (!show) {
      return;
    }
    const onRedirect = () => {
      if (isPageContextReady && !cancellationFailedRef.current) {
        resetUserDetails();
      }
      setSelectedConfirmationNumber?.(null);
    };
    window.addEventListener('beforeunload', onRedirect);
    return () => {
      window.removeEventListener('beforeunload', onRedirect);
    };
  }, [show, isPageContextReady, setSelectedConfirmationNumber]);
  const handleCancellationFailure = () => {
    cancellationFailedRef.current = true;
    setShowCancellationError(true);
  };
  const handleClose = () => {
    resetModalViewState();
    setSelectedConfirmationNumber?.(null);
  };
  const singleRoomCancelCta = buildSingleRoomCta(
    cancelAllRoomsCta?.cancelRoom?.ctaText,
    props?.cancelRoom?.ctaText,
    props?.cancelButtonLabel,
    cancelAllRoomsCta?.cancelRoom?.trackingProperties,
    props?.cancelRoom?.trackingProperties
  );
  const singleRoomKeepCta = buildSingleRoomCta(
    cancelAllRoomsCta?.keepRoom?.ctaText,
    props?.keepRoom?.ctaText,
    props?.keepBookingLabel,
    cancelAllRoomsCta?.keepRoom?.trackingProperties,
    props?.keepRoom?.trackingProperties
  );

  if (!show) {
    return null;
  }

  return (
    <StyledCancelTripModal data-testid="cancel-trip-modal" data-component-name="o-account-cancel-trip-modal">
      <Modal
        show={show}
        handleBlur={true}
        modalId="cancel-trip-modal"
        labelledBy="cancel-trip-modal"
        role="alertdialog"
        aria-modal="true"
        onClose={handleClose}
        setPopupOpenState={handleClose}
        popupOpenState={show}
        disableScrollOnBody={true}
      >
        <Modal.Header
          labelText={props?.cancelModalTitle ?? singleRoomCancelCta.ctaText}
          popupHeaderOnCLoseFunc={handleClose}
          customHeadingClass="t-subtitle-xl"
        />
        <Modal.Body className="canceltrip_modal_body p-0">
          {isOrderLoading && (
            <div className="container modal_loader px-0 py-3">
              <div className="col">
                <SkeletonLoaderV2 className="modal_loader__heading" />
                <SkeletonLoaderV2 className="modal_loader__body mt-3" />
              </div>
            </div>
          )}
          {showCancellationError && uxlFailureErrorMessage && (
            <div className="px-5 py-4">
              <Messages messageType="error-sev1" className="account-page-error-msg">
                <div role="alert">
                  <RichText text={uxlFailureErrorMessage} componentId="cancel-trip-failure-message" />
                </div>
              </Messages>
            </div>
          )}
          {isPageContextReady && orderResponse && CancelRoomModalComponent && (
            <CancelRoomModalComponent
              isInEditor={false}
              currentBooking={props?.currentBooking ?? ''}
              cancelRooms={singleRoomCancelCta}
              keepRooms={singleRoomKeepCta}
              room={cancelAllRoomsCta?.room ?? ''}
              stayDates={cancelAllRoomsCta?.stayDates ?? ''}
              confirmationNumber={cancelAllRoomsCta?.confirmationNumber ?? ''}
              handleClose={handleClose}
              currentConfNum={currentConfNum}
              cancelRoom={singleRoomCancelCta}
              keepRoom={singleRoomKeepCta}
              summaryofCharges={{
                night: cancelAllRoomsCta?.night ?? '',
                avgNight:
                  itemData?.productType?.segments?.[0]?.product?.totalRate?.rateAmountsByMode?.average?.subtotal
                    ?.currency ?? '',
                total: cancelAllRoomsCta?.total ?? '',
                totalForStay: cancelAllRoomsCta?.totalPerRoom ?? '',
                points: cancelAllRoomsCta?.points ?? '',
                estTaxesAndFees: cancelAllRoomsCta?.estimatedGovernmentTaxesAndFees ?? '',
                title: cancelAllRoomsCta?.title ?? '',
                totalCashRate: cancelAllRoomsCta?.totalCashRate ?? '',
                award: cancelAllRoomsCta?.award ?? '',
                awards: cancelAllRoomsCta?.awards ?? '',
              }}
              freeNightCreditMsg={cancelAllRoomsCta?.freeNightCreditMsg ?? ''}
              pointsCreditMsg={cancelAllRoomsCta?.pointsCreditMsg ?? ''}
              suiteNightAwardLabel={cancelAllRoomsCta?.suiteNightAwardLabel ?? ''}
              suiteNightAwardForfeitedLabel={props?.suiteNightAwardForfeitedLabel ?? ''}
              overridePropertyCountry={cancelAllRoomsCta?.overridePropertyCountry ?? false}
              overridePropertyCountryText={props?.overridePropertyCountryText}
              onCancellationFailure={handleCancellationFailure}
            />
          )}
        </Modal.Body>
      </Modal>
    </StyledCancelTripModal>
  );
};
